import { useUpdateCadRunInfo } from '@hooks/useUpdateCADJob';
import { useCallback, useEffect, useState } from 'react';
import { useUploadFiles } from 'src/hooks/useUploadFiles';
import { ProductFile } from 'src/pages/UploadPage/Uploader';

export function useCADUpload(accountId: string, setRunIDParam: (newRunID: string) => void) {
  const {
    mutateAsync: patchCadRunInfo,
    isLoading: isCADJobPatchLoading,
    reset: resetPatchCadRunInfoMutation,
  } = useUpdateCadRunInfo();

  const [uploadedCadFile, setUploadedCadFile] = useState<ProductFile[]>([]);
  const [runID, setRunID] = useState<string>('');

  const {
    mutate,
    filesUploadStatus,
    controller,
    isLoading,
    isError,
    isSuccess,
    reset: resetUploadJobMutation,
  } = useUploadFiles(uploadedCadFile);

  const uploadFile = async () => {
    mutate();
  };

  const handleUploadError = useCallback(async () => {
    setRunIDParam(runID);
    await patchCadRunInfo({ runID, accountId, status: 'IN_PROGRESS_REQUIRES_CAD_FILE' });
  }, [setRunIDParam, runID, patchCadRunInfo, accountId]);

  /* istanbul ignore next */
  const handleCancelUpload = async () => {
    controller.abort();
    setRunIDParam(runID);
    await patchCadRunInfo({ runID, accountId, status: 'IN_PROGRESS_REQUIRES_CAD_FILE' });
    setUploadedCadFile([]);
    setRunID('');
  };

  useEffect(() => {
    if (isError) {
      handleUploadError();
    }
  }, [isError, handleUploadError]);

  const newUploadStatuses = Array.from(filesUploadStatus.entries());

  const isUploadComplete =
    filesUploadStatus.size > 0 && // Check if all files in filesUploadStatus have been successfully uploaded
    Array.from(filesUploadStatus.values()).every((status) => status.isSuccess);

  const reset = () => {
    setRunIDParam(runID);
    resetPatchCadRunInfoMutation();
    resetUploadJobMutation();
    setUploadedCadFile([]);
    setRunID('');
  };

  return {
    isLoading,
    isCADJobPatchLoading,
    uploadFile,
    uploadedCadFile,
    handleCancelUpload,
    newUploadStatuses,
    isUploadComplete,
    setUploadedCadFile,
    runID,
    setRunID,
    isError,
    isSuccess,
    reset,
  };
}
